(() => {
  if (customElements.get('funnel-modal')) return;

  class FunnelModal extends HTMLElement {
    constructor() {
      super();

      this.answers = {};
      this.previouslyFocusedElement = null;
      this.modal = null;
      this.content = null;
      this.steps = [];
      this.currentStepIndex = 0;
      this.currentStepElement = null;
      this.totalStepsElement = null;
      this.progress = null;
      this.progressBar = null;
      this.summary = null;
      this.summaryList = null;
      this.totalPriceElement = null;
      this.mobileNextButton = null;
      this.summaryResizeObserver = null;
      this.summaryValueElements = new Map();
      this.hubspotForm = null;
      this.isHubSpotFormSubmitted = false;
      this.initialPrice = 0;
      this.priceFormatter = null;

      this.handleClick = this.handleClick.bind(this);
      this.handleChange = this.handleChange.bind(this);
      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleHubSpotFormReady = this.handleHubSpotFormReady.bind(this);
      this.handleHubSpotFormSuccess = this.handleHubSpotFormSuccess.bind(this);
    }

    connectedCallback() {
      this.modal = this.querySelector('[data-funnel-modal]');
      this.content = this.querySelector('[data-funnel-content]');
      this.steps = Array.from(this.querySelectorAll('[data-funnel-step]'));
      this.currentStepElement = this.querySelector('[data-funnel-current-step]');
      this.totalStepsElement = this.querySelector('[data-funnel-total-steps]');
      this.progress = this.querySelector('[data-funnel-progress]');
      this.progressBar = this.querySelector('[data-funnel-progress-bar]');
      this.summary = this.querySelector('[data-funnel-summary]');
      this.summaryList = this.querySelector('[data-funnel-summary-list]');
      this.totalPriceElement = this.querySelector('[data-funnel-total-price]');
      this.mobileNextButton = this.querySelector('[data-funnel-mobile-next]');
      this.initialPrice = this.parsePrice(this.dataset.initialPrice);
      this.priceFormatter = this.createPriceFormatter();

      this.addEventListener('click', this.handleClick);
      this.addEventListener('change', this.handleChange);
      this.addEventListener('shopify:block:select', this.handleBlockSelect);
      document.addEventListener('click', this.handleDocumentClick);
      document.addEventListener('keydown', this.handleKeydown);
      window.addEventListener('hs-form-event:on-ready', this.handleHubSpotFormReady);
      window.addEventListener('hs-form-event:on-submission:success', this.handleHubSpotFormSuccess);

      this.steps.forEach((step, index) => this.initializeStep(step, index));
      this.initializeSummary();
      this.initializeSummaryHeight();
      this.steps.forEach((step) => this.updateStepAnswer(step, { emit: false }));

      this.showStep(0);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('change', this.handleChange);
      this.removeEventListener('shopify:block:select', this.handleBlockSelect);
      document.removeEventListener('click', this.handleDocumentClick);
      document.removeEventListener('keydown', this.handleKeydown);
      window.removeEventListener('hs-form-event:on-ready', this.handleHubSpotFormReady);
      window.removeEventListener('hs-form-event:on-submission:success', this.handleHubSpotFormSuccess);
      this.summaryResizeObserver?.disconnect();
    }

    initializeStep(step, index) {
      const isFinalStep = step.dataset.stepType === 'final';
      const isSelect = step.dataset.answerLayout === 'select';
      const selectionType = isSelect ? 'single' : step.dataset.selectionType;
      const groupName = `funnel-${this.dataset.sectionId}-${step.dataset.stepId}`;
      const optionsSource = step.querySelector('[data-funnel-options-source]');
      const optionElements = Array.from(step.querySelectorAll('[data-funnel-option]'));
      const selectWrapper = step.querySelector('[data-funnel-select-wrapper]');
      const select = step.querySelector('[data-funnel-select]');
      const nextButton = step.querySelector('[data-funnel-next]');

      step.dataset.stepIndex = index;
      step.dataset.selectionType = selectionType;

      const stepLabel = step.querySelector('[data-funnel-step-label]');
      const stepNumber = step.querySelector('[data-funnel-step-number]');
      const nextButtonText = step.querySelector('[data-funnel-next-text]');
      const customStepLabel = step.dataset.stepLabelText;

      if (stepLabel) stepLabel.textContent = customStepLabel || this.dataset.stepLabel || 'Step';
      if (stepNumber) stepNumber.textContent = customStepLabel ? '' : index + 1;
      if (nextButtonText) nextButtonText.textContent = this.dataset.nextButtonText || 'Next';

      optionElements.forEach((option) => {
        const input = option.querySelector('input[data-funnel-answer]');

        if (!input) return;

        input.type = selectionType === 'multiple' ? 'checkbox' : 'radio';
        input.name = groupName;
        input.disabled = isSelect;
      });

      if (optionsSource) {
        optionsSource.hidden = isSelect;
      }

      if (selectWrapper && select) {
        selectWrapper.hidden = !isSelect;
        select.disabled = !isSelect;
        select.name = groupName;

        if (isSelect) {
          this.populateSelect(select, optionElements);
        }
      }

      if (nextButton) {
        nextButton.hidden = isFinalStep || index === this.steps.length - 1;
      }
    }

    populateSelect(select, optionElements) {
      const placeholder = document.createElement('option');

      placeholder.value = '';
      placeholder.textContent = select.dataset.placeholder || 'Choose an option';
      placeholder.selected = true;
      placeholder.disabled = true;

      select.replaceChildren(placeholder);

      optionElements.forEach((option) => {
        const selectOption = document.createElement('option');

        selectOption.value = option.dataset.optionValue || '';
        selectOption.textContent = option.dataset.optionLabel || selectOption.value;
        selectOption.dataset.price = option.dataset.optionPrice || '0';
        select.append(selectOption);
      });
    }

    initializeSummary() {
      if (!this.summaryList) return;

      this.summaryList.replaceChildren();
      this.summaryValueElements.clear();

      this.steps.forEach((step) => {
        if (step.dataset.stepType === 'final') return;

        const row = document.createElement('div');
        const label = document.createElement('dt');
        const value = document.createElement('dd');
        const fallbackLabel = step.querySelector('[data-funnel-step-heading]')?.textContent.trim() || '';

        row.className = 'funnel__summary-row is-empty';
        label.className = 'funnel__summary-label';
        value.className = 'funnel__summary-value';
        label.textContent = step.dataset.summaryLabel || fallbackLabel;
        value.textContent = this.dataset.summaryEmptyValue || '–';

        row.append(label, value);
        this.summaryList.append(row);
        this.summaryValueElements.set(step.dataset.stepId, value);
      });

      this.updateSummary();
    }

    initializeSummaryHeight() {
      if (!this.summary || typeof ResizeObserver === 'undefined') return;

      const updateSummaryHeight = () => {
        this.style.setProperty('--funnel-summary-height', `${this.summary.offsetHeight}px`);
      };

      this.summaryResizeObserver = new ResizeObserver(updateSummaryHeight);
      this.summaryResizeObserver.observe(this.summary);
      updateSummaryHeight();
    }

    createPriceFormatter() {
      const locale = this.dataset.locale || document.documentElement.lang || 'de-DE';
      const currency = this.dataset.currencyCode || 'EUR';

      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    }

    parsePrice(value) {
      const price = Number(value);

      return Number.isFinite(price) ? price : 0;
    }

    handleClick(event) {
      const target = event.target;

      if (!(target instanceof Element)) return;

      if (target.closest('[data-funnel-close]')) {
        this.handleBack();
        return;
      }

      const summaryToggle = target.closest('[data-funnel-summary-toggle]');

      if (summaryToggle) {
        this.setSummaryExpanded(summaryToggle.getAttribute('aria-expanded') !== 'true');
        return;
      }

      const nextButton = target.closest('[data-funnel-next]');

      if (nextButton && !nextButton.disabled) {
        this.showStep(this.currentStepIndex + 1, { focus: true });
      }
    }

    handleChange(event) {
      const target = event.target;

      if (!(target instanceof Element) || !target.matches('[data-funnel-answer]')) return;

      const step = target.closest('[data-funnel-step]');

      if (step) {
        this.updateStepAnswer(step);
      }
    }

    handleDocumentClick(event) {
      const target = event.target;

      if (!(target instanceof Element)) return;

      const trigger = target.closest('[data-funnel-open]');

      if (!trigger || trigger.dataset.funnelOpen !== this.id) return;

      this.open();
    }

    handleKeydown(event) {
      if (event.key !== 'Escape' || !this.isOpen()) return;

      this.close({ reset: true });
    }

    handleBlockSelect(event) {
      const target = event.target;

      if (!(target instanceof Element)) return;

      const step = target.closest('[data-funnel-step]');
      const stepIndex = this.steps.indexOf(step);

      if (stepIndex >= 0) {
        this.showStep(stepIndex);
      }
    }

    handleHubSpotFormReady(event) {
      const embeddedFormId = this.querySelector('.hs-form-frame')?.dataset.formId;

      if (!embeddedFormId || event.detail?.formId !== embeddedFormId || !window.HubSpotFormsV4) return;

      this.hubspotForm = window.HubSpotFormsV4.getFormFromEvent(event);
      this.updateHubSpotSummary();
    }

    handleHubSpotFormSuccess(event) {
      const embeddedFormId = this.querySelector('.hs-form-frame')?.dataset.formId;

      if (!embeddedFormId || event.detail?.formId !== embeddedFormId) return;

      this.isHubSpotFormSubmitted = true;
    }

    handleBack() {
      if (this.currentStepIndex === 0 || this.isHubSpotFormSubmitted) {
        this.close({ reset: true });
        return;
      }

      this.showStep(this.currentStepIndex - 1, { focus: true });
    }

    open() {
      if (!this.modal) return;

      this.previouslyFocusedElement = document.activeElement;
      this.modal.hidden = false;
      document.body.classList.add('funnel-modal-open');
      this.content?.focus();
    }

    close({ reset = false } = {}) {
      if (!this.modal) return;

      if (reset) {
        this.reset();
      }

      this.modal.hidden = true;
      document.body.classList.remove('funnel-modal-open');

      if (this.previouslyFocusedElement instanceof HTMLElement) {
        this.previouslyFocusedElement.focus();
      }
    }

    reset() {
      this.querySelectorAll('input[data-funnel-answer]').forEach((input) => {
        input.checked = false;
      });

      this.querySelectorAll('select[data-funnel-answer]').forEach((select) => {
        select.selectedIndex = 0;
      });

      this.answers = {};
      this.isHubSpotFormSubmitted = false;
      this.steps.forEach((step) => this.updateStepAnswer(step, { emit: false }));
      this.setSummaryExpanded(false);
      this.showStep(0);
      this.dispatchEvent(new CustomEvent('funnel:reset', { bubbles: true }));
    }

    updateStepAnswer(step, { emit = true } = {}) {
      const stepId = step.dataset.stepId;
      const selectionType = step.dataset.selectionType;
      const select = step.querySelector('select[data-funnel-answer]:not(:disabled)');
      let selections = [];

      if (select && select.value) {
        selections = [
          {
            value: select.value,
            label: select.selectedOptions[0]?.textContent.trim() || select.value,
            price: this.parsePrice(select.selectedOptions[0]?.dataset.price),
          },
        ];
      } else if (!select) {
        selections = Array.from(step.querySelectorAll('input[data-funnel-answer]:checked:not(:disabled)')).map(
          (input) => ({
            value: input.value,
            label: input.dataset.answerLabel || input.value,
            price: this.parsePrice(input.dataset.answerPrice),
          })
        );
      }

      step.querySelectorAll('[data-funnel-option]').forEach((option) => {
        const input = option.querySelector('input[data-funnel-answer]');
        const isSelected = select ? select.value === option.dataset.optionValue : Boolean(input?.checked);

        option.classList.toggle('is-selected', isSelected);
      });

      if (selections.length) {
        this.answers[stepId] = {
          label: step.dataset.summaryLabel || '',
          value: selectionType === 'multiple' ? selections : selections[0],
        };
      } else {
        delete this.answers[stepId];
      }

      this.updateSummary();

      const nextButton = step.querySelector('[data-funnel-next]');

      if (nextButton) {
        nextButton.disabled = selections.length === 0;
      }

      if (step === this.steps[this.currentStepIndex]) {
        this.updateMobileNextButton();
      }

      if (emit) {
        this.dispatchEvent(
          new CustomEvent('funnel:answer-change', {
            bubbles: true,
            detail: {
              stepId,
              answer: this.answers[stepId] || null,
              answers: { ...this.answers },
            },
          })
        );
      }
    }

    updateSummary() {
      const emptyValue = this.dataset.summaryEmptyValue || '–';
      let totalPrice = this.initialPrice;

      this.steps.forEach((step) => {
        const stepId = step.dataset.stepId;
        const answer = this.answers[stepId];
        const summaryValue = this.summaryValueElements.get(stepId);
        const summaryRow = summaryValue?.closest('.funnel__summary-row');

        summaryRow?.classList.toggle('is-empty', !answer);
        summaryRow?.classList.toggle('is-filled', Boolean(answer));

        if (!answer) {
          if (summaryValue) summaryValue.textContent = emptyValue;
          return;
        }

        const selections = Array.isArray(answer.value) ? answer.value : [answer.value];

        if (summaryValue) {
          summaryValue.textContent = selections.map((selection) => selection.label).join(', ');
        }

        totalPrice += selections.reduce((sum, selection) => sum + this.parsePrice(selection.price), 0);
      });

      if (this.totalPriceElement && this.priceFormatter) {
        this.totalPriceElement.textContent = this.priceFormatter.format(totalPrice);
      }

      this.updateHubSpotSummary();
    }

    getSummaryText() {
      const lines = Array.from(this.summaryList?.querySelectorAll('.funnel__summary-row.is-filled') || []).map(
        (row) => {
          const label = row.querySelector('.funnel__summary-label')?.textContent.trim();
          const value = row.querySelector('.funnel__summary-value')?.textContent.trim();

          return label && value ? `${label}: ${value}` : '';
        }
      );
      const priceLabel = this.querySelector('.funnel__summary-price > span')?.textContent.trim();
      const price = this.querySelector('.funnel__summary-price strong')?.textContent.replace(/\s+/g, ' ').trim();
      const hasPriceNote = this.querySelector('.funnel__summary-note')?.textContent.trim().startsWith('*');

      if (priceLabel && price) {
        lines.push(`${priceLabel}: ${price}${hasPriceNote ? '*' : ''}`);
      }

      return lines.filter(Boolean).join('\n');
    }

    updateHubSpotSummary() {
      const fieldName = this.dataset.hubspotSummaryField?.trim();

      if (!this.hubspotForm || !fieldName) return;

      try {
        // HubSpot's updated forms API expects hidden-field values as an array of strings.
        const update = this.hubspotForm.setFieldValue(fieldName, [this.getSummaryText()]);

        update?.catch?.((error) => console.error('Unable to update the HubSpot funnel summary field.', error));
      } catch (error) {
        console.error('Unable to update the HubSpot funnel summary field.', error);
      }
    }

    setSummaryExpanded(isExpanded) {
      const toggle = this.querySelector('[data-funnel-summary-toggle]');

      this.summary?.classList.toggle('is-open', isExpanded);
      toggle?.setAttribute('aria-expanded', String(isExpanded));
    }

    showStep(index, { focus = false } = {}) {
      if (!this.steps.length) {
        this.updateProgress(0);
        return;
      }

      const nextIndex = Math.min(Math.max(index, 0), this.steps.length - 1);

      this.steps.forEach((step, stepIndex) => {
        step.hidden = stepIndex !== nextIndex;
      });

      this.currentStepIndex = nextIndex;
      this.classList.toggle('is-final-step', this.steps[nextIndex].dataset.stepType === 'final');
      this.updateProgress(nextIndex + 1);
      this.updateMobileNextButton();

      if (focus) {
        const focusTarget = this.steps[nextIndex].querySelector('[data-funnel-step-heading]') || this.steps[nextIndex];
        focusTarget.focus();
      }
    }

    updateProgress(currentStep) {
      const totalSteps = this.steps.length;
      const progressPercentage = totalSteps ? (currentStep / totalSteps) * 100 : 0;

      if (this.currentStepElement) {
        this.currentStepElement.textContent = currentStep;
      }

      if (this.totalStepsElement) {
        this.totalStepsElement.textContent = totalSteps;
      }

      if (this.progress) {
        this.progress.setAttribute('aria-valuemax', totalSteps);
        this.progress.setAttribute('aria-valuenow', currentStep);
      }

      if (this.progressBar) {
        this.progressBar.style.width = `${progressPercentage}%`;
      }
    }

    updateMobileNextButton() {
      if (!this.mobileNextButton) return;

      const currentStep = this.steps[this.currentStepIndex];
      const desktopNextButton = currentStep?.querySelector('[data-funnel-next]');
      const isLastStep = this.currentStepIndex === this.steps.length - 1;
      const isFinalStep = currentStep?.dataset.stepType === 'final';

      this.mobileNextButton.hidden = !currentStep || isLastStep || isFinalStep;
      this.mobileNextButton.disabled = !desktopNextButton || desktopNextButton.disabled;
    }

    isOpen() {
      return Boolean(this.modal && !this.modal.hidden);
    }
  }

  customElements.define('funnel-modal', FunnelModal);
})();
